import { useQuery } from '@tanstack/react-query';
import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect, useRef } from 'react';

import { NoIndexHead } from '@/components/NoIndexHead';
import { Button, ButtonText } from '@/components/ui/button';
import { Heading } from '@/components/ui/heading';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { ROUTES } from '@/constants/routes';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { getPublicExpertProfileV2QueryOptions } from '@/services/getPublicExpertProfileV2';
import { trackExpertLinkOpened } from '@/util/analyticsEvents';
import { readViaTag, takeExpertLinkVia } from '@/util/expertLinkAttribution';

function ExpertLinkNotFound() {
  const { replace } = useRouter();

  return (
    <VStack
      className="flex-1 items-center justify-center gap-3 px-8 py-24"
      accessibilityLiveRegion="polite">
      <NoIndexHead />

      <Heading size="h2-md" className="text-center">
        This link is no longer available
      </Heading>

      <Text className="text-center text-typography-600">
        The expert may have changed or removed it. You can still find them in the directory.
      </Text>

      <Button className="mt-4" onPress={() => replace(ROUTES.DIRECTORY)}>
        <ButtonText>Browse experts</ButtonText>
      </Button>
    </VStack>
  );
}

/**
 * An expert's personal link, `/ask/<slug>`.
 *
 * On web the API serves this URL with the expert's preview tags and the app
 * boots here; on iOS and Android the universal link opens the app straight
 * into it. Either way the slug is resolved to the expert, a retired slug moves
 * to the current one, and the visitor lands on the draft-inquiry screen addressed to that expert.
 * Every branch marks the page `noindex` on web, whatever state it renders in.
 */
export default function ExpertLinkRoute() {
  const { slug, via: viaParam } = useLocalSearchParams<{ slug: string; via?: string }>();

  const { isAuthenticated, isLoading: isAuthLoading } = useAuthStatus();

  const {
    data: profile,
    isLoading,
    isError,
  } = useQuery(getPublicExpertProfileV2QueryOptions(slug));

  /*
   * The platform the link was followed from, resolved once. The entry holder comes first
   * because it is the reliable copy and consuming it stops a later visit inheriting it;
   * the route's own param is the fallback for an entry the holder missed. Kept in a ref
   * so the effect re-running as sign-in state settles reports the same source.
   */
  const viaRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!profile || isAuthLoading) {
      return;
    }

    viaRef.current ??= takeExpertLinkVia(slug) ?? readViaTag(viaParam);

    trackExpertLinkOpened({
      expertId: profile.userId,
      slug: profile.slug,
      isAuthenticated,
      slugWasCurrent: profile.requestedSlugIsCurrent,
      via: viaRef.current,
    });
  }, [profile, isAuthLoading, isAuthenticated, slug, viaParam]);

  if (isLoading || isAuthLoading) {
    return (
      <>
        <NoIndexHead />
        <Spinner size="large" className="absolute bottom-0 left-0 right-0 top-0" />
      </>
    );
  }

  if (isError || !profile) {
    return <ExpertLinkNotFound />;
  }

  if (!profile.requestedSlugIsCurrent) {
    return <Redirect href={ROUTES.EXPERT_LINK(profile.slug)} />;
  }

  return (
    <Redirect
      href={{
        pathname: isAuthenticated
          ? '/(secure)/(tabs)/(directory)/directory/inquiry/[id]'
          : '/(open)/directory/inquiry/[id]',
        params: { id: String(profile.userId) },
      }}
    />
  );
}
